import {
  Boxes,
  Images,
  LayoutDashboard,
  Layers,
  Megaphone,
  PackageCheck,
  Scale,
  ShoppingBag,
  Settings2,
  TrendingUp,
  UsersRound,
  WalletCards,
  Workflow,
  type LucideIcon,
} from "lucide-react"

export const SIDEBAR_STORAGE_KEY = "nova-sidebar"

export const SIDEBAR_PREFERENCE = {
  expanded: "expanded",
  collapsed: "collapsed",
} as const

export type NavigationItemId =
  | "dashboard"
  | "orders"
  | "runs"
  | "queues"
  | "storage-refresh"
  | "products"
  | "studio"
  | "keywords"
  | "campaigns"
  | "pnl"
  | "payouts"
  | "users"
  | "settings"

export type NavigationItem = {
  id: NavigationItemId
  to: string
  label: string
  icon: LucideIcon
}

export type NavigationGroup = {
  label: string | null
  items: readonly NavigationItem[]
}

export const PRIMARY_NAVIGATION_GROUPS = [
  {
    label: null,
    items: [{ id: "dashboard", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Work",
    items: [
      { id: "orders", to: "/orders", label: "Orders", icon: ShoppingBag },
      { id: "runs", to: "/runs", label: "Runs", icon: Workflow },
      {
        id: "queues",
        to: "/queues",
        label: "Queues",
        icon: Layers,
      },
      {
        id: "storage-refresh",
        to: "/storage-refresh",
        label: "Storage refresh",
        icon: PackageCheck,
      },
    ],
  },
  {
    label: "Catalog",
    items: [
      { id: "products", to: "/products", label: "Products", icon: Boxes },
      { id: "studio", to: "/studio", label: "Studio", icon: Images },
    ],
  },
  {
    label: "Insights",
    items: [
      { id: "keywords", to: "/keywords", label: "Keywords", icon: TrendingUp },
      { id: "campaigns", to: "/campaigns", label: "Campaigns", icon: Megaphone },
      { id: "pnl", to: "/pnl", label: "Profit & loss", icon: Scale },
      { id: "payouts", to: "/payouts", label: "Payouts", icon: WalletCards },
    ],
  },
] as const satisfies readonly NavigationGroup[]

export const MANAGE_NAVIGATION_GROUP = {
  label: "Manage",
  items: [
    { id: "users", to: "/users", label: "Users", icon: UsersRound },
    { id: "settings", to: "/settings/schedules", label: "Settings", icon: Settings2 },
  ],
} as const satisfies NavigationGroup

const ACTIVE_NAVIGATION_MATCHERS = [
  { id: "dashboard", matches: (pathname: string) => isPathOwnedBy(pathname, "/dashboard") },
  { id: "orders", matches: (pathname: string) => isPathOwnedBy(pathname, "/orders") },
  { id: "runs", matches: (pathname: string) => isPathOwnedBy(pathname, "/runs") },
  {
    id: "queues",
    matches: (pathname: string) => isPathOwnedBy(pathname, "/queues"),
  },
  {
    id: "storage-refresh",
    matches: (pathname: string) => isPathOwnedBy(pathname, "/storage-refresh"),
  },
  { id: "products", matches: (pathname: string) => isPathOwnedBy(pathname, "/products") },
  { id: "studio", matches: (pathname: string) => isPathOwnedBy(pathname, "/studio") },
  { id: "keywords", matches: (pathname: string) => isPathOwnedBy(pathname, "/keywords") },
  { id: "campaigns", matches: (pathname: string) => isPathOwnedBy(pathname, "/campaigns") },
  { id: "pnl", matches: (pathname: string) => isPathOwnedBy(pathname, "/pnl") },
  { id: "payouts", matches: (pathname: string) => isPathOwnedBy(pathname, "/payouts") },
  { id: "users", matches: (pathname: string) => isPathOwnedBy(pathname, "/users") },
  { id: "settings", matches: (pathname: string) => isPathOwnedBy(pathname, "/settings") },
] as const satisfies readonly {
  id: NavigationItemId
  matches: (pathname: string) => boolean
}[]

export function readSidebarPreference(value: string | null): boolean {
  return value !== SIDEBAR_PREFERENCE.collapsed
}

export function activeNavigationItem(pathname: string): NavigationItemId | null {
  return ACTIVE_NAVIGATION_MATCHERS.find(({ matches }) => matches(pathname))?.id ?? null
}

function isPathOwnedBy(pathname: string, rootPath: string): boolean {
  return pathname === rootPath || pathname.startsWith(`${rootPath}/`)
}

export function isSidebarShortcut(event: KeyboardEvent, isMac: boolean): boolean {
  if (
    event.repeat ||
    event.key.toLowerCase() !== "b" ||
    event.altKey ||
    event.shiftKey ||
    isEditableShortcutTarget(event.target)
  ) {
    return false
  }
  return isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey
}

export function isEditableShortcutTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) {
    return true
  }
  return target.closest('[contenteditable]:not([contenteditable="false"])') !== null
}
